import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, ArrowRight, MapPin } from "lucide-react";
import api from "../../lib/api";
import { imageFor } from "../../lib/thrones";
import { Reveal } from "./Reveal";

export const RoyalReel = () => {
  const [titles, setTitles] = React.useState([]);
  const [i, setI] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    api.get("/royal-titles").then((r) => setTitles(r.data || [])).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (paused || titles.length <= 1) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % titles.length), 4000);
    return () => clearInterval(id);
  }, [paused, titles.length]);

  if (titles.length === 0) return null;
  const t = titles[i % titles.length];

  return (
    <section className="bg-night text-cream" data-testid="home-royal-reel">
      <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Royal Yorùbáland · Oyè Ọba</p>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.08] mb-5">
            62 Thrones. 13 Kingdoms. One Heritage.
          </h2>
          <p className="text-cream/70 text-lg leading-relaxed mb-8 max-w-lg">
            From the sacred Ọọ̀ni of Ifẹ̀ to the imperial Aláàfin of Ọ̀yọ́, explore the great crowned
            rulers of Yorùbáland — their kingdoms, titles and significance.
          </p>
          <Link
            to="/royal-titles"
            data-testid="home-royal-reel-cta"
            className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors"
          >
            Explore all 62 thrones
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="relative rounded-3xl overflow-hidden border border-gold/25 aspect-video"
            data-testid="home-royal-reel-card"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={t.id}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="absolute inset-0"
              >
                <img src={imageFor(t.region)} alt={`${t.title} regalia`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,20,15,0.15) 0%, rgba(15,20,15,0.55) 55%, rgba(15,20,15,0.95) 100%)" }} />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 text-gold text-xs font-semibold px-3 py-1 mb-3">
                    <MapPin className="w-3.5 h-3.5" /> {t.region}
                  </span>
                  <p className="uppercase tracking-[0.2em] text-[11px] text-gold-soft mb-1">{t.town}</p>
                  <h3 className="font-serif text-2xl md:text-3xl text-gold leading-snug" data-testid="home-royal-reel-title">{t.title}</h3>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="absolute top-4 right-4 flex gap-1.5">
              {titles.slice(0, Math.min(titles.length, 8)).map((_, idx) => (
                <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i % 8 ? "w-5 bg-gold" : "w-1.5 bg-cream/30"}`} />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default RoyalReel;
